/**
 * The plant search: a box, and under it, while something is typed, what it
 * found, to tap.
 *
 * It used to narrow the Plant drop-down only, so typing changed nothing on
 * the screen until the list was opened, and a variety's name ("Cherokee
 * Purple") or a plant Grow lacks ("zinnia") answered with nothing at all.
 * Now the results are listed under the box, each a full-height row, and a
 * search that finds nothing says so, with whatever the caller offers
 * instead (adding it as the gardener's own variety, say).
 *
 * It is a combobox: the box holds the focus, the arrow keys move through
 * the results and Enter takes the one marked, which is the first until
 * another is. The results are a listbox the box controls.
 *
 * Used by PlantIdentityFields (adding a plant, a seed packet's plants) and
 * by the photo tray's "What is this?" (PileCard), so both read the same.
 *
 * With something typed, an X at the end of the box clears it (E18).
 *
 * A caller can start it with words in it, focused, and add a quiet line under the results (note): E18, a gardener's
 * to-do words carried to Add a plant.
 *
 * @module PlantSearchField
 */

import React from 'react';
import { X } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';

/** One result: what it is, and what it belongs to ("Sun Gold · Vining Cherry Tomato"). */
export interface PlantSearchOption {
  /** Unique among the results; handed back when it is chosen. */
  key: string;
  /** Its name. */
  label: string;
  /** Said after the label, quieter; none for a crop. */
  detail?: string | null;
}

/** The box, its results, and what to do when one is chosen. */
interface PlantSearchFieldProps {
  /** The ids of the box and its list start with this. */
  idPrefix: string;
  /** What is typed in the box. */
  query: string;
  /** Called as the box changes. */
  onQueryChange: (query: string) => void;
  /** What the search found, best first; the caller keeps it short. */
  options: PlantSearchOption[];
  /** A result chosen, by its key. The caller sets the fields and clears the search. */
  onChoose: (key: string) => void;
  /** The box's hint, in the app's language. */
  placeholder: string;
  /** True while a save is under way. */
  disabled?: boolean;
  /** Under the box when the search found nothing. */
  empty?: React.ReactNode;
  /** For the box, where its text is smaller (the photo tray). */
  inputClassName?: string;
  /** True to focus the box as it appears (E18: words carried in from a to-do). */
  autoFocus?: boolean;
  /** A quiet line under the results (or under the box when nothing was found); none for none. */
  note?: string;
}

/** The search box and the results under it; nothing under it while the box is empty. */
export const PlantSearchField: React.FC<PlantSearchFieldProps> = ({
  idPrefix, query, onQueryChange, options, onChoose, placeholder, disabled, empty, inputClassName, autoFocus = false, note,
}) => {
  const { reactTranslate: t } = useTranslation();
  const listId = `${idPrefix}-search-results`;
  const typed = query.trim() !== '';
  const open = typed && options.length > 0;
  // The result Enter takes: the first, until the arrow keys move it.
  const [active, setActive] = React.useState(0);
  React.useEffect(() => { setActive(0); }, [query]);
  const marked = open ? Math.min(active, options.length - 1) : -1;

  /** The arrow keys move through the results, Enter takes the marked one rather than sending the form. */
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!open) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((marked + step + options.length) % options.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      onChoose(options[marked].key);
    }
  };

  return (
    <>
      <div className="relative mt-1.5">
        <Input
          id={`${idPrefix}-search`}
          type="search"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open ? `${listId}-${marked}` : undefined}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label={placeholder}
          disabled={disabled}
          className={cn('[&::-webkit-search-cancel-button]:appearance-none', typed && 'pr-10', inputClassName)}
          autoComplete="off"
          autoFocus={autoFocus}
        />
        {typed && !disabled && (
          <button
            type="button"
            aria-label={t('plants.form.fields.clearSearch')}
            onClick={() => { onQueryChange(''); document.getElementById(`${idPrefix}-search`)?.focus(); }}
            className="absolute right-0 top-1/2 flex h-8 w-10 -translate-y-1/2 items-center justify-center text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        )}
      </div>
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={t('plants.form.fields.searchResults')}
          className="mt-1 overflow-hidden rounded-sm border bg-card py-1 shadow-sm"
        >
          {options.map((option, n) => (
            <li
              key={option.key}
              id={`${listId}-${n}`}
              role="option"
              aria-selected={n === marked}
              // Keeps the focus in the box, so the keys still work after a tap elsewhere in the list.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onChoose(option.key)}
              className={cn(
                'flex min-h-11 cursor-pointer items-center px-3 text-[0.9375rem] hover:bg-primary/10',
                n === marked && 'bg-primary/10',
              )}
            >
              <span className="min-w-0 truncate">
                <span className="font-medium">{option.label}</span>
                {option.detail && <span className="text-muted-foreground"> · {option.detail}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {typed && options.length === 0 && empty && <div className="mt-1.5">{empty}</div>}
      {note && <p className="mt-1.5 text-sm text-muted-foreground">{note}</p>}
    </>
  );
};

/**
 * What a search that found nothing says: "No plant called "zinnia" in Grow
 * yet", as a status a screen reader reads out.
 */
export const NoPlantFound: React.FC<{ query: string; children?: React.ReactNode }> = ({ query, children }) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <>
      <p className="text-sm text-muted-foreground" role="status">{t('plants.form.fields.noMatch', { query: query.trim() })}</p>
      {children}
    </>
  );
};
